// "Añadir oficina" — the Oficinas screen's form for a new office of the client.
//
// What a YAML entry of the client's registry carries, typed here instead: código, nombre, the
// municipality — searched by name in DIVIPOLA (`BuscadorMunicipio`), or its DANE code typed; the code is
// what is sent and where the map puts it — regional, acceso (URBANA / AÉREA, the matrix's column
// the report to the bank carries) and cupo, the office's own addresses (optional) and, for a client
// with GLORY machines, its "Máquina GLORY" switch — on by default; off asks why, with the same
// reasons as the switch on the list. `createCierreOffice` writes the office, its switch and its
// profile at once, so it is read from its next closing. The words and the payload are
// `utils/cierreNuevaOficina.js`; a refusal stays in the dialog through `correctionError`.
import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Switch,
  Textarea,
  cn,
} from "@mantis-sas/ui";

import { createCierreOffice } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { MAX_DETALLE_GLORY, MOTIVOS_GLORY } from "../../../utils/cierreGlory";
import {
  ACCESOS,
  FORMULARIO_VACIO,
  daneDeMunicipio,
  erroresNuevaOficina,
  municipioDeDane,
  oficinaParaCrear,
  buscarMunicipios,
  cupoConFormato,
} from "../../../utils/cierreNuevaOficina";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const errorDe = (response) =>
  response?.errorCode || response?.message ? correctionError(response) : "No se pudo crear la oficina.";

const CAMPO =
  "h-9 w-full rounded-[8px] border bg-card px-2.5 text-[13px] text-brinks-foreground outline-none focus:border-brinks-border-strong";

function Campo({ id, etiqueta, error, ayuda, children, guiaTour }) {
  return (
    <div {...guiaTour} className="grid content-start gap-1">
      <Label htmlFor={id} className="text-[12px] font-medium text-brinks-muted-foreground">
        {etiqueta}
      </Label>
      {children}
      {error ? (
        <span className="text-[11.5px] text-brinks-danger">{error}</span>
      ) : ayuda ? (
        <span className="text-[11.5px] text-brinks-muted-foreground">{ayuda}</span>
      ) : null}
    </div>
  );
}

/**
 * The municipality search: what is typed filters DIVIPOLA by name (`buscarMunicipios`) into a list
 * under the field, picked with the mouse or ↑ ↓ Enter; a picked one reads "TUNJA, BOYACÁ · 15001",
 * and five digits typed are a code as they are. Drawn in the dialog rather than as a `<datalist>`,
 * whose popup each browser draws its own way, outside the page's look.
 */
function BuscadorMunicipio({ id, valor, disabled, onChange }) {
  const [abierta, setAbierta] = useState(false);
  const [activa, setActiva] = useState(0);
  const sugerencias = abierta ? buscarMunicipios(valor) : [];
  const elegida = sugerencias.some((o) => o.etiqueta === valor);

  const elegir = (o) => {
    onChange(o.etiqueta);
    setAbierta(false);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        role="combobox"
        aria-expanded={sugerencias.length > 0 && !elegida}
        aria-controls={`${id}-lista`}
        autoComplete="off"
        className="bg-card text-brinks-foreground"
        value={valor}
        disabled={disabled}
        placeholder="Escribe el nombre, ej: Tunja"
        onFocus={() => setAbierta(true)}
        onBlur={() => setAbierta(false)}
        onChange={(e) => {
          onChange(e.target.value);
          setActiva(0);
          setAbierta(true);
        }}
        onKeyDown={(e) => {
          if (!sugerencias.length || elegida) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiva((i) => Math.min(i + 1, sugerencias.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiva((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            elegir(sugerencias[Math.min(activa, sugerencias.length - 1)]);
          } else if (e.key === "Escape") {
            setAbierta(false);
          }
        }}
      />
      {sugerencias.length > 0 && !elegida ? (
        <ul
          id={`${id}-lista`}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-[8px] border bg-card py-1 shadow-md"
        >
          {sugerencias.map((o, i) => (
            <li
              key={o.codigo}
              role="option"
              aria-selected={i === activa}
              // mousedown, not click: the input's blur would close the list before a click lands.
              onMouseDown={(e) => {
                e.preventDefault();
                elegir(o);
              }}
              onMouseEnter={() => setActiva(i)}
              className={cn(
                "flex cursor-pointer items-baseline justify-between gap-3 px-2.5 py-1.5 text-[12.5px] text-brinks-foreground",
                i === activa && "bg-brinks-surface-hover"
              )}
            >
              <span className="min-w-0 truncate">{o.etiqueta.split(" · ")[0]}</span>
              <span className="shrink-0 tabular-nums text-brinks-muted-foreground">{o.codigo}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * `regiones` are the client's regions on screen; `codigosExistentes` its office codes, so a taken one
 * is said before sending. `conGlory` false for a client without the switch. `onCreated` gets the
 * backend's answer.
 */
export function NuevaOficinaDialog({
  open,
  token,
  mantisBusinessId,
  clientCode,
  regiones = [],
  codigosExistentes = [],
  conGlory = false,
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState(FORMULARIO_VACIO);
  const [tocado, setTocado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(FORMULARIO_VACIO);
    setTocado(false);
    setError("");
  }, [open]);

  const errores = erroresNuevaOficina(form, codigosExistentes);
  const valido = Object.keys(errores).length === 0;
  const visible = (campo) => (tocado ? errores[campo] : undefined);
  const lugar = municipioDeDane(form.dane);
  const poner = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const guardar = async () => {
    setTocado(true);
    if (!valido) return;
    setGuardando(true);
    setError("");
    const response = await createCierreOffice(
      token,
      mantisBusinessId,
      clientCode,
      oficinaParaCrear(form, { conGlory })
    ).catch(() => null);
    setGuardando(false);
    if (response?.success) onCreated(response.data);
    else setError(errorDe(response));
  };

  return (
    <Dialog open={open} onOpenChange={(abierto) => !abierto && !guardando && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader className="text-left">
          <div className="flex items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border bg-brinks-surface text-brinks-foreground">
              <Building2 className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">Añadir oficina</DialogTitle>
                <BotonGuia guiaId="dialogo-nueva-oficina" titulo="Añadir oficina" texto="Registra una oficina nueva del cliente con sus datos, sus correos y su máquina GLORY. Aparece de inmediato en Oficinas." />
              </div>
              <DialogDescription className="mt-1 text-[12.5px] text-brinks-muted-foreground">
                La oficina se lee desde su próximo cierre, con los documentos que pide su perfil.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
            <Campo guiaTour={guia("Código de oficina", "El código con el que el banco identifica la oficina en sus archivos.")} id="oficina-codigo" etiqueta="Código de oficina" error={visible("officeCode")}>
              <Input
                id="oficina-codigo"
                className="bg-card text-brinks-foreground"
                maxLength={64}
                value={form.officeCode}
                disabled={guardando}
                onChange={poner("officeCode")}
                placeholder="Ej: 9650"
              />
            </Campo>
            <Campo guiaTour={guia("Nombre", "El nombre con el que se verá la oficina en la plataforma.")} id="oficina-nombre" etiqueta="Nombre de la oficina" error={visible("name")}>
              <Input
                id="oficina-nombre"
                className="bg-card text-brinks-foreground"
                maxLength={120}
                value={form.name}
                disabled={guardando}
                onChange={poner("name")}
                placeholder="Ej: Tunja Centro"
              />
            </Campo>
          </div>

          <Campo
            guiaTour={guia("Municipio", "Búscalo por nombre y elígelo de la lista. Con su código DANE se ubica la oficina en el mapa.")}
            id="oficina-municipio"
            etiqueta="Municipio (código DANE)"
            error={visible("dane")}
            ayuda={
              lugar
                ? `${lugar.municipio}, ${lugar.departamento} · DANE ${form.dane}`
                : "Elígelo de la lista. Con él se ubica en el mapa."
            }
          >
            <BuscadorMunicipio
              id="oficina-municipio"
              valor={form.municipio}
              disabled={guardando}
              onChange={(municipio) => setForm((f) => ({ ...f, municipio, dane: daneDeMunicipio(municipio) }))}
            />
          </Campo>

          <div className="grid gap-3 sm:grid-cols-3">
            <Campo guiaTour={guia("Regional", "La regional del cliente a la que pertenece la oficina.")} id="oficina-region" etiqueta="Regional" error={visible("region")}>
              <select
                id="oficina-region"
                className={CAMPO}
                value={form.region}
                disabled={guardando}
                onChange={poner("region")}
              >
                <option value="">Elige la regional</option>
                {regiones.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo guiaTour={guia("Acceso", "Opcional: si se llega a la oficina por vía urbana o aérea.")} id="oficina-acceso" etiqueta="Acceso (opcional)">
              <select
                id="oficina-acceso"
                className={CAMPO}
                value={form.acceso}
                disabled={guardando}
                onChange={poner("acceso")}
              >
                <option value="">Sin indicar</option>
                {ACCESOS.map((a) => (
                  <option key={a.valor} value={a.valor}>
                    {a.etiqueta}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo guiaTour={guia("Cupo", "Opcional: el cupo de la oficina en pesos.")} id="oficina-cupo" etiqueta="Cupo (opcional)" ayuda="En pesos">
              <Input
                id="oficina-cupo"
                inputMode="numeric"
                className="bg-card text-brinks-foreground"
                value={form.cupo}
                disabled={guardando}
                onChange={(e) => setForm((f) => ({ ...f, cupo: cupoConFormato(e.target.value) }))}
                placeholder="Ej: 300.000.000"
              />
            </Campo>
          </div>

          <Campo
            guiaTour={guia("Correos", "Opcional: las direcciones de la oficina, separadas por comas. A ellas se envía su cuadre.")}
            id="oficina-correos"
            etiqueta="Correos (opcional)"
            error={errores.correos}
            ayuda="Uno por línea, o separados por comas. Le llega a la oficina su propio cuadre."
          >
            <Textarea
              id="oficina-correos"
              rows={2}
              value={form.correos}
              disabled={guardando}
              onChange={poner("correos")}
              placeholder="Ej: oficina@bancoagrario.gov.co"
              className="text-[12.5px]"
            />
          </Campo>

          {conGlory ? (
            <div {...guia("Máquina GLORY", "Encendida por defecto. Si la oficina no tiene máquina o está dañada, apágala y elige el motivo.")} className="grid gap-2 rounded-[8px] border px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-brinks-foreground">Máquina GLORY</p>
                  <p className="text-[11.5px] text-brinks-muted-foreground">
                    {form.gloryEncendida
                      ? "Se le pedirá y leerá su GLORY."
                      : "No se le pedirá la GLORY: su arqueo será el conteo manual firmado."}
                  </p>
                </div>
                <Switch
                  checked={form.gloryEncendida}
                  disabled={guardando}
                  onCheckedChange={(valor) => setForm((f) => ({ ...f, gloryEncendida: valor }))}
                  aria-label="Máquina GLORY de la nueva oficina"
                />
              </div>
              {form.gloryEncendida ? null : (
                <>
                  <RadioGroup
                    value={form.reasonCode}
                    onValueChange={(valor) => setForm((f) => ({ ...f, reasonCode: valor }))}
                    disabled={guardando}
                    className="grid gap-1.5"
                  >
                    {MOTIVOS_GLORY.map((m) => (
                      <label
                        key={m.codigo}
                        htmlFor={`nueva-motivo-${m.codigo}`}
                        className={cn(
                          "flex cursor-pointer items-start gap-2.5 rounded-[8px] border px-3 py-1.5 transition",
                          form.reasonCode === m.codigo
                            ? "border-brinks-border-strong bg-brinks-surface"
                            : "hover:bg-brinks-surface-hover"
                        )}
                      >
                        <RadioGroupItem id={`nueva-motivo-${m.codigo}`} value={m.codigo} className="mt-0.5" />
                        <span className="min-w-0">
                          <span className="block text-[13px] font-medium text-brinks-foreground">{m.etiqueta}</span>
                          <span className="block text-[11.5px] text-brinks-muted-foreground">{m.ayuda}</span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                  {form.reasonCode === "OTHER" ? (
                    <Textarea
                      aria-label="Por qué no tiene GLORY"
                      rows={2}
                      maxLength={MAX_DETALLE_GLORY}
                      value={form.note}
                      disabled={guardando}
                      onChange={poner("note")}
                      placeholder="Escribe el motivo"
                      className="text-[12.5px]"
                    />
                  ) : null}
                  {visible("glory") ? (
                    <span className="text-[11.5px] text-brinks-danger">{visible("glory")}</span>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>

        {error ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {error}
          </p>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-9" disabled={guardando} onClick={onClose}>
            Cancelar
          </Button>
          <Button {...guia("Crear oficina", "Crea la oficina con su configuración. Si algo no es válido, se marca en su campo.")} className="h-9 gap-1.5" disabled={guardando || (tocado && !valido)} onClick={guardar}>
            {guardando ? <Spinner size="sm" /> : <Building2 className="h-4 w-4" />} Crear oficina
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
